import React, { useEffect, useRef, useState } from 'react';
import type { ApprovalItem, TeamSecretPolicyV1 } from '../client';
import AsyncButton from '../components/AsyncButton';
import { ApprovalCardHeader } from '../approvals/ApprovalCardHeader';
import { SafeSecretPolicy } from './AgentSecrets';

interface SecretApprovalCardProps {
  approval: ApprovalItem;
  deciding?: boolean;
  headerless?: boolean;
  onDecide: (
    decision: 'approved' | 'rejected',
    note: string,
    inputs?: Record<string, unknown>,
  ) => Promise<void>;
}

interface AvailableSecret {
  id: string;
  currentName: string;
  description?: string;
}

const SafeField: React.FC<{
  label: string;
  value: React.ReactNode;
  code?: boolean;
}> = ({ label, value, code }) => (
  <div className="secret-approval-field">
    <span>{label}</span>
    {code ? <code>{value}</code> : <p>{value}</p>}
  </div>
);

const SecretApprovalCard: React.FC<SecretApprovalCardProps> = ({
  approval,
  deciding,
  headerless,
  onDecide,
}) => {
  const [note, setNote] = useState('');
  const [pending, setPending] = useState<'approved' | 'rejected' | null>(null);
  const [createMode, setCreateMode] = useState<'existing' | 'new' | null>(null);
  const [existingSecretId, setExistingSecretId] = useState('');
  const [secretValue, setSecretValue] = useState('');
  const inFlight = useRef(false);
  const detail = approval.detail || {};
  const mutation = approval.type === 'secret_mutation';
  const create = approval.type === 'secret_create';
  const proposedName = String(detail.proposedName || 'proposed secret');
  const availableSecrets = Array.isArray(detail.availableSecrets)
    ? (detail.availableSecrets as AvailableSecret[])
    : [];

  useEffect(() => {
    setCreateMode(null);
    setExistingSecretId('');
    setSecretValue('');
  }, [approval.id]);

  if (approval.status !== 'pending') {
    const resolvedBy = String(
      approval.decidedBy?.name || detail.resolvedBy || '',
    ).trim();
    const actualName = String(
      detail.actualName || detail.currentName || detail.proposedName || '—',
    );
    const outcome =
      approval.status === 'approved'
        ? `Approved${resolvedBy ? ` by ${resolvedBy}` : ''}`
        : approval.status === 'rejected'
          ? `Rejected${resolvedBy ? ` by ${resolvedBy}` : ''}`
          : `This request is ${approval.status}`;
    return (
      <article
        className="approval-card approval-card-secret approval-card-secret-resolved"
        data-testid={`approval-${approval.id}`}>
        {!headerless && (
          <ApprovalCardHeader
            teamId={approval.teamId}
            agent={approval.requestingAgent}
            requestingUser={approval.requestingUser}
            requestingTrigger={approval.requestingTrigger}
            origin={approval.origin}
            createdAt={approval.createdAt}
            title={approval.title}
          />
        )}
        <div className="secret-approval-ledger">
          <SafeField
            label={
              approval.status === 'approved'
                ? 'Approved secret name'
                : 'Requested secret name'
            }
            value={actualName}
            code
          />
          {detail.proposedDescription && (
            <SafeField
              label="Description"
              value={String(detail.proposedDescription)}
            />
          )}
        </div>
        <div
          className={`approval-secret-status approval-secret-status-${approval.status}`}
          role="status">
          {outcome}
          {approval.decisionNote ? ` — ${approval.decisionNote}` : '.'}
        </div>
      </article>
    );
  }

  const decide = async (decision: 'approved' | 'rejected') => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(decision);
    try {
      const inputs =
        decision !== 'approved' || !create
          ? undefined
          : createMode === 'existing'
            ? { existingSecretId }
            : { value: secretValue };
      await onDecide(decision, note.trim(), inputs);
      setNote('');
      setSecretValue('');
    } finally {
      inFlight.current = false;
      setPending(null);
    }
  };

  return (
    <article
      className="approval-card approval-card-secret"
      data-testid={`approval-${approval.id}`}>
      {!headerless && (
        <ApprovalCardHeader
          teamId={approval.teamId}
          agent={approval.requestingAgent}
          requestingUser={approval.requestingUser}
          requestingTrigger={approval.requestingTrigger}
          origin={approval.origin}
          createdAt={approval.createdAt}
          title={approval.title}
        />
      )}

      <div className="secret-approval-ledger">
        {create ? (
          <>
            <SafeField label="Proposed name" value={proposedName} code />
            <SafeField
              label="Description"
              value={String(detail.proposedDescription || '—')}
            />
            {detail.policy && (
              <SafeSecretPolicy policy={detail.policy as TeamSecretPolicyV1} />
            )}
            <fieldset className="secret-create-choice">
              <legend>Provide this secret</legend>
              <label>
                <input
                  type="radio"
                  name={`secret-source-${approval.id}`}
                  checked={createMode === 'existing'}
                  disabled={
                    deciding ||
                    pending !== null ||
                    availableSecrets.length === 0
                  }
                  onChange={() => setCreateMode('existing')}
                />
                Use an existing team secret
              </label>
              <select
                aria-label="Existing team secret"
                value={existingSecretId}
                disabled={
                  deciding ||
                  pending !== null ||
                  createMode !== 'existing' ||
                  availableSecrets.length === 0
                }
                onChange={(event) => setExistingSecretId(event.target.value)}>
                <option value="">Select a team secret…</option>
                {availableSecrets.map((secret) => (
                  <option key={secret.id} value={secret.id}>
                    {secret.currentName}
                    {secret.description ? ` — ${secret.description}` : ''}
                  </option>
                ))}
              </select>
              <label>
                <input
                  type="radio"
                  name={`secret-source-${approval.id}`}
                  checked={createMode === 'new'}
                  disabled={deciding || pending !== null}
                  onChange={() => setCreateMode('new')}
                />
                Create {proposedName}
              </label>
              <input
                aria-label="Secret value"
                type="password"
                autoComplete="new-password"
                value={secretValue}
                disabled={deciding || pending !== null || createMode !== 'new'}
                onChange={(event) => setSecretValue(event.target.value)}
                placeholder={`Value for ${proposedName}`}
              />
            </fieldset>
          </>
        ) : mutation ? (
          <>
            <div className="secret-approval-comparison">
              <div>
                <span className="secret-eyebrow">Current</span>
                <SafeField
                  label="Name"
                  value={String(detail.currentName || '—')}
                  code
                />
                <SafeField
                  label="Description"
                  value={String(detail.currentDescription || '—')}
                />
              </div>
              <div>
                <span className="secret-eyebrow">Proposed</span>
                <SafeField
                  label="Name"
                  value={String(
                    detail.proposedName ?? detail.currentName ?? '—',
                  )}
                  code
                />
                <SafeField
                  label="Description"
                  value={String(
                    detail.proposedDescription ??
                      detail.currentDescription ??
                      '—',
                  )}
                />
              </div>
            </div>
            <SafeField
              label="Value will change"
              value={detail.valueWillChange === true ? 'Yes' : 'No'}
            />
          </>
        ) : (
          <>
            <SafeField
              label="Secret"
              value={String(detail.currentName || '—')}
              code
            />
            <SafeField
              label="Description"
              value={String(detail.description || '—')}
            />
            {detail.policy && (
              <SafeSecretPolicy policy={detail.policy as TeamSecretPolicyV1} />
            )}
          </>
        )}
      </div>

      <div className="approval-reason">
        <span>The agent's stated reason</span>
        <p>
          {approval.reason ||
            detail.reason ||
            (approval.legacyReasonUnavailable
              ? 'Reason unavailable for this older request.'
              : '')}
        </p>
      </div>
      <label className="approval-note">
        <span>
          Note to agent <small>(optional)</small>
        </span>
        <textarea
          aria-label="Note to agent"
          maxLength={4000}
          disabled={deciding || pending !== null}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Add context, constraints, or next steps…"
        />
      </label>
      <div className="approval-actions">
        <AsyncButton
          type="button"
          className="approval-reject"
          disabled={deciding || pending !== null}
          loading={pending === 'rejected'}
          loadingText="Rejecting..."
          onClick={() => decide('rejected')}>
          Reject
        </AsyncButton>
        <AsyncButton
          type="button"
          className="approval-approve"
          disabled={
            deciding ||
            pending !== null ||
            (create &&
              (createMode === null ||
                (createMode === 'existing' && !existingSecretId) ||
                (createMode === 'new' && secretValue.length === 0)))
          }
          loading={pending === 'approved'}
          loadingText="Approving..."
          onClick={() => decide('approved')}>
          Approve
        </AsyncButton>
      </div>
    </article>
  );
};

export default SecretApprovalCard;
